import { renderColor } from '../../constants/Constants'
import { getDimensionWeightsForOverallDonut } from '../../helpers/donut'
import {
  getLocalizedCityName,
  getLocalizedCountryName,
  getLocalizedProvinceName,
  getLocalizedProvinceLabel,
} from '../../helpers/lang'
import { renderDonut } from '../../pages/DashboardMultiCity/Helpers'
import useLanguage from '../../state/hooks/useLanguage'
import LazyDonut from './LazyDonut'
import {
  faAngleDown,
  faAngleUp,
  faInfinity,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Input, Select, FormControl, InputLabel } from '@material-ui/core'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import React, { useEffect, useMemo, useState } from 'react'
import { injectIntl } from 'react-intl'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'

/* Rows visible when the menu first opens; the rest draw as they scroll in. */
const EAGER_ROW_COUNT = 12

function CityMenu({
  hideSelect,
  selectedCityId,
  cities,
  cityCountryMap,
  updateCurrentCityId,
  selectedQuarter,
  toggleMultiCityView,
  handlePageChange,
  isMultiCity,
  pageStore,
  intl,
}) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [sortedCityCountryMap, setSortedCityCountryMap] = useState(null)
  const [allCitiesList, setAllCitiesList] = useState({})
  const [searchKey, setSearchKey] = useState('')
  const [filterCountry, setFilterCountry] = useState('')
  const [filterState, setFilterState] = useState('')
  const { language } = useLanguage()
  const isLoading =
    pageStore.getIn(['isLoadingSingleCityInfo']) ||
    pageStore.getIn(['isLoadingMultiCityInfo'])
  useEffect(() => {
    const cityEntries = cityCountryMap?.obj

    if (!cityEntries) {
      setAllCitiesList([])
      return
    }

    /*
     * Index the city map by id once, rather than rescanning it for every
     * subscribed city. With ~1300 cities the nested scan was ~1.8M iterations.
     */
    const entryById = new Map()
    Object.values(cityEntries).forEach((entry) => {
      entryById.set(entry.id, entry)
    })

    const matchedCities = []
    Object.values(cities).forEach((city) => {
      if (!city) return
      const entry = entryById.get(city.city_id)
      if (entry) matchedCities.push(entry)
    })

    const sortedCitiesCountries = matchedCities.sort((p1, p2) => {
      if (p1.name > p2.name) return 1
      if (p1.name < p2.name) return -1
      return 0
    })

    setAllCitiesList(sortedCitiesCountries)
  }, [cities, cityCountryMap])

  useEffect(() => {
    if (cityCountryMap?.arr && Array.isArray(cityCountryMap.arr)) {
      const sortedCitiesCountries = cityCountryMap.arr.sort((p1, p2) => {
        if (p1.name > p2.name) return 1
        if (p1.name < p2.name) return -1
        return 0
      })
      setSortedCityCountryMap(sortedCitiesCountries)
    }
  }, [cityCountryMap, selectedQuarter])

  const countryOptions = useMemo(() => {
    if (!allCitiesList || !allCitiesList.length) return []
    const countries = [
      ...new Set(
        allCitiesList
          .map((c) => getLocalizedCountryName(c, language))
          .filter(Boolean)
      ),
    ]
    return countries.sort()
  }, [allCitiesList, language])

  const stateOptions = useMemo(() => {
    if (!filterCountry || !allCitiesList || !allCitiesList.length) return []
    const states = [
      ...new Set(
        allCitiesList
          .filter(
            (c) =>
              getLocalizedCountryName(c, language) === filterCountry &&
              getLocalizedProvinceName(c, language)
          )
          .map((c) => getLocalizedProvinceName(c, language))
      ),
    ]
    return states.sort()
  }, [allCitiesList, filterCountry, language])

  const stateLabel = useMemo(() => {
    if (!filterCountry || !allCitiesList || !allCitiesList.length) return ''
    const cityInCountry = allCitiesList.find(
      (c) =>
        getLocalizedCountryName(c, language) === filterCountry &&
        getLocalizedProvinceLabel(c, language)
    )
    return cityInCountry
      ? getLocalizedProvinceLabel(cityInCountry, language)
      : ''
  }, [allCitiesList, filterCountry, language])

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setSearchKey('')
    setFilterCountry('')
    setFilterState('')
    setAnchorEl(null)
  }

  const getCityIQ = (city) => {
    if (city.id === 'all') return city.iq
    if (cities && cities[city.id] && cities[city.id].iq) {
      return cities[city.id].iq
    }
    return 0
  }

  const getSelectedCityInfo = (selectedCityId) => {
    if (isMultiCity)
      return {
        id: 'all',
        name: intl.formatMessage({ id: 'cityMenu.all' }),
        iq: 100,
      }
    for (let i = 0; i < (sortedCityCountryMap || []).length; i++) {
      if (parseInt(sortedCityCountryMap[i].id) === parseInt(selectedCityId)) {
        return {
          ...sortedCityCountryMap[i],
          iq: getCityIQ(sortedCityCountryMap[i]),
        }
      }
    }
    return {}
  }

  const renderMenuItem = (cityInfo, { lazy = false, eager = false } = {}) => {
    const { iq, id } = cityInfo
    const provinceName = getLocalizedProvinceName(cityInfo, language)
    const countryName = getLocalizedCountryName(cityInfo, language)
    const drawDonut = () =>
      renderDonut({
        data: getDimensionWeightsForOverallDonut(cities, id),
        colors: [1, 2, 3, 4, 5]
          .map((d) => renderColor(d).main)
          .concat(['#444']),
        label: id === 'all' ? null : Math.round(iq),
        donutSize: 55,
        fontSize: 25,
        iconLabel: id === 'all' ? faInfinity : null,
      })
    return (
      <div key={`content_${id}`} className="appbarCityMenuItemContent">
        <div className="appBarScore">
          {iq &&
            (lazy ? (
              <LazyDonut size={55} render={drawDonut} eager={eager} />
            ) : (
              drawDonut()
            ))}
        </div>
        <div className="appbarCityName">
          <div className="city">{getLocalizedCityName(cityInfo, language)}</div>
          <div className="country">
            {provinceName ? `${provinceName}, ${countryName}` : countryName}
          </div>
        </div>
      </div>
    )
  }

  const selectedCity = getSelectedCityInfo(selectedCityId)

  if (hideSelect) {
    return (
      <div className="appbarCityMenuItemWrapper">
        {renderMenuItem({
          ...selectedCity,
          dimensionWeights: getDimensionWeightsForOverallDonut(
            cities,
            selectedCityId
          ),
        })}
      </div>
    )
  }

  const mayFilterBySearch = (cities) => {
    return cities.filter((city) => {
      if (city.id === 'all') return true
      if (
        filterCountry &&
        getLocalizedCountryName(city, language) !== filterCountry
      )
        return false
      if (
        filterState &&
        getLocalizedProvinceName(city, language) !== filterState
      )
        return false
      if (searchKey.length < 3) return true
      return (
        (city.name ?? '').toLowerCase().includes(searchKey.toLowerCase()) ||
        (city.country ?? '').toLowerCase().includes(searchKey.toLowerCase())
      )
    })
  }

  const visibleCities = mayFilterBySearch(
    [
      {
        id: 'all',
        name: intl.formatMessage({ id: 'cityMenu.all' }),
        iq: 100,
      },
    ].concat(allCitiesList || [])
  )

  return (
    <div className="appbarCityMenu">
      <div
        aria-controls="simple-menu"
        aria-haspopup="true"
        onClick={handleClick}
        className="appbarCityMenuOpen"
      >
        {renderMenuItem({
          ...selectedCity,
          dimensionWeights: getDimensionWeightsForOverallDonut(
            cities,
            selectedCityId
          ),
        })}
        <FontAwesomeIcon icon={Boolean(anchorEl) ? faAngleUp : faAngleDown} />
      </div>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        className="appbarCityMenuDropdown"
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
      >
        <Input
          key="search"
          fullWidth
          placeholder={intl.formatMessage({ id: 'cityMenu.searchBy' })}
          onChange={(e) => {
            setSearchKey(e.target.value)
          }}
          value={searchKey}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '12px',
            width: '326px',
          }}
        />
        <div style={{ display: 'flex', gap: '0.5rem', padding: '0.5rem 1rem' }}>
          <FormControl style={{ flex: 1, minWidth: 0 }} size="small">
            <InputLabel shrink style={{ fontSize: '11px' }}>
              {intl.formatMessage({ id: 'cityMenu.country' })}
            </InputLabel>
            <Select
              native
              value={filterCountry}
              onChange={(e) => {
                setFilterCountry(e.target.value)
                setFilterState('')
                  }}
              style={{ fontSize: '11px', marginTop: '16px' }}
            >
              <option value="">All</option>
              {countryOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl style={{ flex: 1, minWidth: 0 }} size="small">
            <InputLabel shrink style={{ fontSize: '11px' }}>
              {stateLabel || intl.formatMessage({ id: 'cityMenu.state' })}
            </InputLabel>
            <Select
              native
              value={filterState}
              onChange={(e) => {
                setFilterState(e.target.value)
                  }}
              style={{ fontSize: '11px', marginTop: '16px' }}
              disabled={!filterCountry}
            >
              <option value="">All</option>
              {stateOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </FormControl>
        </div>
        {visibleCities.map((city, index) => {
          return (
            <MenuItem
              key={`item_${city.id}`}
              className={`appbarCityMenuItem ${
                selectedCityId === city.id && !isMultiCity ? 'selected' : ''
              }`}
              onClick={() => {
                handleClose()
                switch (city.id) {
                  case 'all':
                    if (isMultiCity) return
                    toggleMultiCityView(true)
                    handlePageChange('dashboard')
                    break
                  default:
                    updateCurrentCityId(city.id)
                    if (!isMultiCity) return
                    toggleMultiCityView(false)
                    handlePageChange('dashboard')
                    break
                }
              }}
            >
              {renderMenuItem(
                {
                  ...city,
                  iq: city ? getCityIQ(city) : null,
                  dimensionWeights: getDimensionWeightsForOverallDonut(
                    cities,
                    city.id
                  ),
                },
                { lazy: true, eager: index < EAGER_ROW_COUNT }
              )}
            </MenuItem>
          )
        })}
      </Menu>
    </div>
  )
}
function mapStateToProps(state) {
  return {
    pageStore: state.PageStore,
  }
}

export default injectIntl(withRouter(connect(mapStateToProps)(CityMenu)))
